import {
  CloudUploadOutlined,
  DeleteOutlined,
  ErrorOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Page } from 'shared/ui/Page';

import { useExternalMatchesPage } from '../hooks/useExternalMatchesPage';

export default () => {
  const { t } = useTranslation();
  const {
    stats,
    isStatsLoading,
    suppliers,
    isSuppliersLoading,
    handleUpload,
    isUploading,
    uploadJobId,
    uploadStatus,
    isUploadStatusLoading,
    clearUploadStatus,
    refetchStats,
  } = useExternalMatchesPage();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sourceName, setSourceName] = useState('internal_team');
  const [sourceType, setSourceType] = useState<'internal' | 'supplier'>(
    'internal',
  );
  const [selectedSupplierId, setSelectedSupplierId] = useState<
    string | undefined
  >();
  const [flushExisting, setFlushExisting] = useState(false);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setSelectedFile(file);
      }
    },
    [],
  );

  const handleSourceTypeChange = useCallback(
    (newSourceType: 'internal' | 'supplier') => {
      setSourceType(newSourceType);
      if (newSourceType === 'internal') {
        setSelectedSupplierId(undefined);
        setSourceName('internal_team');
      } else {
        setSourceName('');
      }
    },
    [],
  );

  const handleSupplierChange = useCallback(
    (supplierId: string | undefined) => {
      setSelectedSupplierId(supplierId);
      if (supplierId && suppliers) {
        const supplier = suppliers.find((s) => s.id === supplierId);
        if (supplier) {
          setSourceName(supplier.name.toLowerCase().replace(/\s+/g, '_'));
        }
      }
    },
    [suppliers],
  );

  const handleUploadClick = useCallback(async () => {
    if (!selectedFile) return;

    try {
      await handleUpload(
        selectedFile,
        sourceName,
        sourceType,
        selectedSupplierId,
        flushExisting,
      );
    } catch (error) {
      // Error is handled by the hook
    }
  }, [
    selectedFile,
    sourceName,
    sourceType,
    selectedSupplierId,
    flushExisting,
    handleUpload,
  ]);

  const isUploadDisabled =
    !selectedFile ||
    !sourceName ||
    (sourceType === 'supplier' && !selectedSupplierId) ||
    isUploading;

  return (
    <Page>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('External Matches')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('Manage external SKU matches from internal teams and suppliers.')}
        </Typography>

        <Grid container spacing={3}>
          {/* Statistics Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography variant="h6">{t('Match Statistics')}</Typography>
                  <Button
                    startIcon={<RefreshOutlined />}
                    onClick={refetchStats}
                    disabled={isStatsLoading}
                    size="small"
                  >
                    {t('Refresh')}
                  </Button>
                </Box>

                {isStatsLoading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : stats ? (
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('Total Matches')}
                      </Typography>
                      <Typography variant="h6">
                        {stats.totalMatches.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('Internal Matches')}
                      </Typography>
                      <Typography variant="h6">
                        {stats.internalMatches.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('Supplier Matches')}
                      </Typography>
                      <Typography variant="h6">
                        {stats.supplierMatches.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('Exact Matches')}
                      </Typography>
                      <Typography variant="h6">
                        {stats.exactMatches.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('Alternate Matches')}
                      </Typography>
                      <Typography variant="h6">
                        {stats.alternateMatches.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('Unknown Matches')}
                      </Typography>
                      <Typography variant="h6">
                        {stats.unknownMatches.toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Alert severity="error">
                    {t('Failed to load statistics')}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Upload Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('Upload External Matches')}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <input
                    accept=".csv,.xlsx,.xls"
                    style={{ display: 'none' }}
                    id="file-upload"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadOutlined />}
                      fullWidth
                      sx={{ mb: 1 }}
                    >
                      {selectedFile
                        ? selectedFile.name
                        : t('Choose File (CSV/Excel)')}
                    </Button>
                  </label>
                </Box>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{t('Source Type')}</InputLabel>
                  <Select
                    value={sourceType}
                    label={t('Source Type')}
                    onChange={(e) =>
                      handleSourceTypeChange(
                        e.target.value as 'internal' | 'supplier',
                      )
                    }
                  >
                    <MenuItem value="internal">{t('Internal')}</MenuItem>
                    <MenuItem value="supplier">{t('Supplier')}</MenuItem>
                  </Select>
                </FormControl>

                {sourceType === 'internal' && (
                  <TextField
                    label={t('Source Name')}
                    value={sourceName}
                    onChange={(e) => setSourceName(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                    placeholder={t('e.g., internal_team, quality_assurance')}
                  />
                )}

                {sourceType === 'supplier' && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{t('Supplier')}</InputLabel>
                    <Select
                      value={selectedSupplierId || ''}
                      label={t('Supplier')}
                      onChange={(e) =>
                        handleSupplierChange(
                          e.target.value
                            ? (e.target.value as string)
                            : undefined,
                        )
                      }
                      disabled={isSuppliersLoading}
                    >
                      {suppliers?.map((supplier) => (
                        <MenuItem key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={flushExisting}
                      onChange={(e) => setFlushExisting(e.target.checked)}
                    />
                  }
                  label={t(
                    'Flush existing matches from this source before uploading',
                  )}
                  sx={{ mb: 2 }}
                />

                <Button
                  variant="contained"
                  onClick={handleUploadClick}
                  disabled={isUploadDisabled}
                  fullWidth
                  startIcon={
                    isUploading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <CloudUploadOutlined />
                    )
                  }
                >
                  {isUploading ? t('Uploading...') : t('Upload Matches')}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Upload Progress */}
          {uploadJobId && uploadStatus && (
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Typography variant="h6">{t('Upload Progress')}</Typography>
                    <Button
                      startIcon={<DeleteOutlined />}
                      onClick={clearUploadStatus}
                      size="small"
                      color="error"
                    >
                      {t('Clear')}
                    </Button>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">
                        {t('Status')}: {uploadStatus.status}
                      </Typography>
                      <Typography variant="body2">
                        {uploadStatus.progress}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={uploadStatus.progress}
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {uploadStatus.processedRows} {t('of')}{' '}
                      {uploadStatus.totalRows} {t('rows processed')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {uploadStatus.matchesCreated} {t('matches created')}
                    </Typography>
                  </Box>

                  {uploadStatus.errors.length > 0 && (
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: 'error.light',
                        color: 'error.contrastText',
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        <ErrorOutlined
                          sx={{ mr: 1, verticalAlign: 'middle' }}
                        />
                        {t('Errors')} ({uploadStatus.errors.length})
                      </Typography>
                      <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                        {uploadStatus.errors
                          .slice(0, 10)
                          .map((error, index) => (
                            <Typography
                              key={index}
                              variant="body2"
                              sx={{ mb: 0.5 }}
                            >
                              {t('Row')} {error.row}: {error.error}
                            </Typography>
                          ))}
                        {uploadStatus.errors.length > 10 && (
                          <Typography
                            variant="body2"
                            sx={{ fontStyle: 'italic' }}
                          >
                            {t('... and')} {uploadStatus.errors.length - 10}{' '}
                            {t('more errors')}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  )}

                  {uploadStatus.status === 'completed' && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      {t('Upload completed successfully!')}{' '}
                      {uploadStatus.matchesCreated} {t('matches were created.')}
                    </Alert>
                  )}

                  {uploadStatus.status === 'failed' && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {t(
                        'Upload failed. Please check the errors above and try again.',
                      )}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </Page>
  );
};
